import { useContext, useEffect } from "react";
import { Col } from "react-bootstrap";
import BillingHeaderRight from "../../outgoing/SubComponents/BillHeaderRight";
import BillingHeaderLeft from "../../outgoing/SubComponents/BillHeaderLeft";
import SubmitButtons from "../../shared/Form/FormFields/Submitbuttons";
import DocumentConnectionButtons from "../../../../../../layout/Documents/DocumentConnectionButtons";
import SettingsContext from "@serie3/common/auth/SettingsContext";

const LOCALE_CURRENCY = {
  "de-DE": "EUR", "de-AT": "EUR", "fr-FR": "EUR", "es-ES": "EUR",
  "it-IT": "EUR", "nl-NL": "EUR",
  "de-CH": "CHF",
  "en-US": "USD",
  "en-GB": "GBP",
  "pl-PL": "PLN",
};

const inputStyle = {
  width: "100%", padding: "8px 12px", border: "1px solid #e0e0e0",
  borderRadius: "8px", fontSize: "0.92rem", color: "#212121",
  background: "#fff", outline: "none", boxSizing: "border-box",
};

const labelStyle = {
  fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.08em", color: "#9e9e9e", marginBottom: "6px", display: "block",
};

const Section = ({ title, children, action }) => (
  <div style={{
    background: "#fff", borderRadius: "12px",
    boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
    padding: "20px 24px", marginBottom: "14px",
    border: "1px solid transparent",
  }}>
    {(title || action) && (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: "16px",
      }}>
        {title && (
          <div style={{
            fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.08em", color: "#9e9e9e",
          }}>{title}</div>
        )}
        {action}
      </div>
    )}
    {children}
  </div>
);

const FieldGroup = ({ label, children }) => (
  <div style={{ marginBottom: "14px" }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

const Toggle = ({ label, name, formik, onChange }) => {
  const checked = !!formik.values[name];
  return (
    <label style={{
      display: "flex", alignItems: "center", gap: "10px",
      cursor: "pointer", userSelect: "none", marginBottom: "12px",
    }}>
      <span style={{ position: "relative", display: "inline-block", width: "36px", height: "20px", flexShrink: 0 }}>
        <input
          type="checkbox"
          name={name}
          checked={checked}
          onChange={(e) => {
            formik.handleChange(e);
            formik.setFieldValue(name, e.target.checked);
            if (onChange) onChange(e.target.checked);
          }}
          style={{ opacity: 0, width: 0, height: 0, position: "absolute" }}
        />
        <span style={{
          position: "absolute", inset: 0, borderRadius: "20px",
          background: checked ? "#1a73e8" : "#e0e0e0", transition: "background 0.2s",
        }} />
        <span style={{
          position: "absolute", width: "14px", height: "14px", borderRadius: "50%",
          background: "#fff", top: "3px", left: checked ? "19px" : "3px",
          transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }} />
      </span>
      <span style={{ fontSize: "0.88rem", fontWeight: 500, color: "#424242" }}>{label}</span>
    </label>
  );
};

const BillingData = ({ formik, bill, setShowNew, address, setAddress, contact, setContact, loading, onContactUpdated }) => {
  const { settings } = useContext(SettingsContext);

  useEffect(() => {
    const currency = formik.values.currency;
    if (!currency) return;
    const localeCurrency = LOCALE_CURRENCY[settings?.locale] ?? "EUR";
    if (currency.toUpperCase() !== localeCurrency) {
      formik.setFieldValue("exclude_from_vat", true);
    }
  }, [formik.values.currency]);

  return (
    <Col className="col-sm-5" style={{ paddingLeft: "8px", position: "relative" }}>
      {loading && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 10,
          background: "rgba(255,255,255,0.75)", backdropFilter: "blur(2px)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: "12px", borderRadius: "12px",
        }}>
          <div style={{
            width: "36px", height: "36px", borderRadius: "50%",
            border: "3px solid #e8f0fe", borderTopColor: "#1a73e8",
            animation: "spin 0.8s linear infinite",
          }} />
          <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#1a73e8" }}>
            Loading…
          </span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* General */}
      <Section
        title="General"
        action={<DocumentConnectionButtons showUpload={setShowNew} showLink={setShowNew} />}
      >
        <BillingHeaderLeft
          bill={bill}
          contact={contact}
          setContact={setContact}
          setAddress={setAddress}
          addressJson={address}
          formik={formik}
          onContactUpdated={onContactUpdated}
        />
        <FieldGroup label="Subject">
          <input
            type="text" name="betreff" placeholder="Subject"
            value={formik.values.betreff || ""} onChange={formik.handleChange}
            style={inputStyle}
          />
        </FieldGroup>
        <FieldGroup label="Description">
          <textarea
            name="beschreibung" placeholder="Description"
            value={formik.values.beschreibung || ""} onChange={formik.handleChange}
            rows={3} style={{ ...inputStyle, resize: "vertical" }}
          />
        </FieldGroup>
      </Section>

      {/* Amounts */}
      <Section title="Amounts">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <FieldGroup label="Net">
            <input
              type="text" name="netto" placeholder="0.00"
              value={formik.values.netto || ""} onChange={formik.handleChange}
              style={inputStyle}
            />
          </FieldGroup>
          <FieldGroup label="Gross">
            <input
              type="text" name="brutto" placeholder="0.00"
              value={formik.values.brutto || ""} onChange={formik.handleChange}
              style={inputStyle}
            />
          </FieldGroup>
          <FieldGroup label="Tax Amount">
            <div style={{ position: "relative" }}>
              <input
                type="text" name="tax" placeholder="0.00"
                value={formik.values.tax || ""} onChange={formik.handleChange}
                style={{ ...inputStyle, paddingRight: "60px" }}
              />
              {formik.values.netto > 0 && formik.values.tax > 0 && (
                <span style={{
                  position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)",
                  fontSize: "0.78rem", fontWeight: 700, color: "#1a73e8", pointerEvents: "none",
                }}>
                  {((formik.values.tax / formik.values.netto) * 100).toFixed(1)}%
                </span>
              )}
            </div>
          </FieldGroup>
          <FieldGroup label="Currency">
            <input
              type="text" name="currency" placeholder="EUR"
              value={formik.values.currency || ""} onChange={formik.handleChange}
              style={inputStyle}
            />
          </FieldGroup>
        </div>
      </Section>

      {/* Region */}
      <Section title="Region">
        <FieldGroup label="Country">
          <input
            type="text" name="country" placeholder="Country"
            value={formik.values.country || ""} onChange={formik.handleChange}
            style={inputStyle}
          />
        </FieldGroup>
      </Section>

      {/* Dates */}
      <Section title="Dates">
        <BillingHeaderRight formik={formik} />
        <FieldGroup label="Payment Date">
          <input
            type="date" name="paymentdate"
            value={formik.values.paymentdate || ""}
            onChange={formik.handleChange}
            style={inputStyle}
          />
        </FieldGroup>
      </Section>

      {/* Status */}
      <Section title="Status">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px" }}>
          <Toggle label="Draft" name="draft" formik={formik}
            onChange={(val) => { if (val) formik.setFieldValue("geldeingang", false); }}
          />
          <Toggle label="Paid" name="geldeingang" formik={formik}
            onChange={(val) => { if (val) formik.setFieldValue("draft", false); }}
          />
          <Toggle label="Private" name="privat" formik={formik} />
          <Toggle label="Will be debited" name="autopay" formik={formik} />
          <Toggle label="Exclude from VAT" name="exclude_from_vat" formik={formik} />
          {bill?.imported === true && (
            <Toggle label="Imported" name="imported" formik={formik} />
          )}
        </div>
        {formik.values.autopay && (
          <div style={{
            marginTop: "12px", padding: "14px 16px",
            background: "#f4f7ff", borderRadius: "10px", border: "1px solid #dde6ff",
          }}>
            <FieldGroup label="Debit date">
              <input
                type="date" name="autopay_date"
                value={formik.values.autopay_date || ""}
                onChange={formik.handleChange}
                style={{ ...inputStyle, background: "#fff" }}
              />
            </FieldGroup>
          </div>
        )}
      </Section>

      <SubmitButtons formik={formik} bill={bill} forceShow />
    </Col>
  );
};

export default BillingData;
